import { Box, Button, CircularProgress, Link, Paper, Typography } from '@mui/material'
import { useReducer, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useSaveDraftJourneyMutation, useGetCarriersQuery, useGetServicesQuery } from '../../../app/redux-fetch/apiOrders'
import { messages } from '../messages'
import rootEnum from '../../../components/Routes/rootEnum'
import { RootState } from '../../../app/store/store'
import { clearSelection } from './state/journeyPlannerSlice'
import { journeyPlannerReducer } from './state/journeyPlannerReducer'
import { createInitialPlannerState, JourneyPlannerState, EditedLoadActionConfig } from './plannerTypes'
import LegSection from './components/LegSection'
import WaypointSection from './components/WaypointSection'
import SharedWaypointSection from './components/SharedWaypointSection'
import TransportModeIcon from './components/TransportModeIcon'
import AddWaypointSidePanel from './components/AddWaypointSidePanel'
import DeleteWaypointDialog from './components/DeleteWaypointDialog'
import SplitRouteSidePanel from './components/SplitRouteSidePanel'
import MergeRouteSidePanel from './components/MergeRouteSidePanel'
import AddDestinationSidePanel from './components/AddDestinationSidePanel'
import EditActionsServicesSidePanel from './components/EditActionsServicesSidePanel'
import { formatDateTimeISO, formatDateISO } from '../utils'

const transformStateToApiPayload = (state: JourneyPlannerState) => {
  return {
    tenantId: state.tenantId,
    journeyStatusTypeId: state.journeyStatusTypeId,
    legs: state.legs.map((leg) => ({
      id: leg.id > 0 ? leg.id : undefined,
      journeyId: leg.journeyId > 0 ? leg.journeyId : undefined,
      sequence: leg.sequence,
      transportModeId: leg.transportModeId || undefined,
      startJourneyWaypointId: leg.startJourneyWaypointId || undefined,
      endJourneyWaypointId: leg.endJourneyWaypointId || undefined,
      routes: leg.routes.map((route) => ({
        id: route.id > 0 ? route.id : undefined,
        legId: route.legId > 0 ? route.legId : undefined,
        displayOrder: route.displayOrder,
        carrierId: route.carrierId || undefined,
        transitIdentifier: route.transitIdentifier || undefined,
        scheduledDepartureDate: route.scheduledDepartureDate
          ? formatDateTimeISO(route.scheduledDepartureDate)
          : undefined,
        scheduledArrivalDate: route.scheduledArrivalDate
          ? formatDateTimeISO(route.scheduledArrivalDate)
          : undefined,
        destinations: route.destinations.map((dest) => ({
          id: dest.id > 0 ? dest.id : undefined,
          routeId: dest.routeId > 0 ? dest.routeId : undefined,
          locationId: dest.locationId || undefined,
          sequence: dest.sequence,
          scheduledDate: dest.scheduledDate
            ? formatDateISO(dest.scheduledDate)
            : undefined,
          isBoundary: dest.isBoundary,
          loadActions: dest.loadActions.map((action) => ({
            id: action.id > 0 ? action.id : undefined,
            routeDestinationId: action.routeDestinationId > 0 ? action.routeDestinationId : undefined,
            loadId: action.loadId,
            destinationActionTypeId: action.destinationActionTypeId,
          })),
          loadServices: dest.loadServices.map((service) => ({
            id: service.id > 0 ? service.id : undefined,
            routeDestinationId: service.routeDestinationId > 0 ? service.routeDestinationId : undefined,
            loadId: service.loadId,
            serviceId: service.serviceId,
          })),
        })),
      })),
    })),
    waypoints: state.waypoints.map((wp) => ({
      id: wp.id > 0 ? wp.id : undefined,
      journeyId: wp.journeyId > 0 ? wp.journeyId : undefined,
      locationId: wp.locationId || undefined,
      sequence: wp.sequence,
    })),
  }
}

const JourneyPlannerPage = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const { tenantId } = useLoggedInUser()

  const { selectedLoads, loadMetadataById, consignmentRoutes } = useSelector(
    (state: RootState) => state.journeyPlannerSelection
  )

  const [saveDraftJourney, { isLoading: isSaving }] = useSaveDraftJourneyMutation()
  const { data: carriers } = useGetCarriersQuery({ tenantId }, { skip: !tenantId })
  const { data: services = [] } = useGetServicesQuery({ tenantId }, { skip: !tenantId })

  const [plannerState, plannerDispatch] = useReducer(
    journeyPlannerReducer,
    createInitialPlannerState(tenantId)
  )

  const [addWaypointPanelOpen, setAddWaypointPanelOpen] = useState(false)
  const [addWaypointPosition, setAddWaypointPosition] = useState<'before' | 'after'>('after')
  const [addWaypointLegIndex, setAddWaypointLegIndex] = useState(0)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteWaypointIndex, setDeleteWaypointIndex] = useState(0)
  const [deleteLegBeforeNumber, setDeleteLegBeforeNumber] = useState(0)
  const [deleteLegAfterNumber, setDeleteLegAfterNumber] = useState(0)

  const [splitRoutePanelOpen, setSplitRoutePanelOpen] = useState(false)
  const [splitRouteLegIndex, setSplitRouteLegIndex] = useState(0)
  const [splitRouteIndex, setSplitRouteIndex] = useState(0)

  const [mergeRoutePanelOpen, setMergeRoutePanelOpen] = useState(false)
  const [mergeRouteLegIndex, setMergeRouteLegIndex] = useState(0)

  const [addDestinationPanelOpen, setAddDestinationPanelOpen] = useState(false)
  const [addDestinationLegIndex, setAddDestinationLegIndex] = useState(0)
  const [addDestinationRouteIndex, setAddDestinationRouteIndex] = useState(0)
  const [addDestinationInsertAfterIndex, setAddDestinationInsertAfterIndex] = useState(0)

  const [editActionsPanelOpen, setEditActionsPanelOpen] = useState(false)
  const [editActionsLegIndex, setEditActionsLegIndex] = useState(0)
  const [editActionsRouteIndex, setEditActionsRouteIndex] = useState(0)
  const [editActionsDestIndex, setEditActionsDestIndex] = useState(0)

  useEffect(() => {
    if (tenantId && selectedLoads.length > 0 && consignmentRoutes.length > 0) {
      plannerDispatch({ type: 'SET_LOAD_METADATA', loadMetadataById })
      // Create a separate leg for each consignment route
      // Routes are already sorted by earliest scheduled time in the slice
      for (const route of consignmentRoutes) {
        plannerDispatch({
          type: 'ADD_LEG',
          transportModeId: route.transportModeId,
          selectedLoadIds: route.loadIds,
          waypointLocations: route.waypointLocations,
        })
      }
    }
  }, [tenantId, selectedLoads.length, loadMetadataById, consignmentRoutes])

  const handleBackToUnassignedLoads = () => {
    dispatch(clearSelection())
    navigate(rootEnum.JOURNEY)
  }

    const handleAddWaypoint= (afterLegIndex: number) => {
    plannerDispatch({ type: 'ADD_WAYPOINT', afterLegIndex })
  }

  const handleOpenAddWaypointPanel = (position: 'before' | 'after', legIndex: number) => {
    setAddWaypointPosition(position)
    setAddWaypointLegIndex(legIndex)
    setAddWaypointPanelOpen(true)
  }

  const handleCloseAddWaypointPanel = () => {
    setAddWaypointPanelOpen(false)
  }

  const handleAddWaypointWithLeg = (
    locationId: number,
    locationName: string,
    address: string,
    transportModeId: number
  ) => {
    plannerDispatch({
      type: 'ADD_WAYPOINT_WITH_LEG',
      position: addWaypointPosition,
      legIndex: addWaypointLegIndex,
      locationId,
      locationName,
      address,
      transportModeId,
    })
  }

  const handleOpenDeleteDialog = (waypointIndex: number, legBeforeNumber: number, legAfterNumber: number) => {
    setDeleteWaypointIndex(waypointIndex)
    setDeleteLegBeforeNumber(legBeforeNumber)
    setDeleteLegAfterNumber(legAfterNumber)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
  }

  const handleConfirmDeleteWaypoint = (legToRemove: 'before' | 'after') => {
    plannerDispatch({
      type: 'DELETE_WAYPOINT_WITH_LEG',
      waypointIndex: deleteWaypointIndex,
      legToRemove,
    })
  }

  const getWaypointsForLeg = (legIndex: number) => {
    return plannerState.waypoints.filter((wp) => wp.afterLegIndex === legIndex)
  }

  const handleOpenSplitRoutePanel = (legIndex: number, routeIndex: number) => {
    setSplitRouteLegIndex(legIndex)
    setSplitRouteIndex(routeIndex)
    setSplitRoutePanelOpen(true)
  }

  const handleCloseSplitRoutePanel = () => {
    setSplitRoutePanelOpen(false)
  }

  const handleSplitRoute = (
    selectedLoadIds: number[],
    carrierId: number | undefined,
    transitIdentifier: string,
    scheduledDeparture: string | undefined,
    scheduledArrival: string | undefined,
    splitType: 'onePerConsignment' | 'allConsignments'
  ) => {
    const carrier = carrierId
      ? carriers?.find((c) => c.id === carrierId)
      : undefined

    if (splitType === 'onePerConsignment') {
      plannerDispatch({
        type: 'SPLIT_ROUTE_ONE_PER_CONSIGNMENT',
        legIndex: splitRouteLegIndex,
        routeIndex: splitRouteIndex,
        selectedLoadIds,
        carrierId,
        carrierName: carrier?.name,
        transitIdentifier: transitIdentifier || undefined,
        scheduledDepartureDate: scheduledDeparture,
        scheduledArrivalDate: scheduledArrival,
      })
    } else {
      plannerDispatch({
        type: 'SPLIT_ROUTE_ALL_CONSIGNMENTS',
        legIndex: splitRouteLegIndex,
        routeIndex: splitRouteIndex,
        selectedLoadIds,
        carrierId,
        carrierName: carrier?.name,
        transitIdentifier: transitIdentifier || undefined,
        scheduledDepartureDate: scheduledDeparture,
        scheduledArrivalDate: scheduledArrival,
      })
    }
  }

  const handleOpenMergeRoutePanel = (legIndex: number) => {
    setMergeRouteLegIndex(legIndex)
    setMergeRoutePanelOpen(true)
  }

  const handleCloseMergeRoutePanel = () => {
    setMergeRoutePanelOpen(false)
  }

  const handleMergeRoutes = (
    selectedRouteIndices: number[],
    carrierId: number | undefined,
    carrierName: string | undefined,
    transitIdentifier: string,
    scheduledDeparture: string | undefined,
    scheduledArrival: string | undefined
  ) => {
    plannerDispatch({
      type: 'MERGE_ROUTES',
      legIndex: mergeRouteLegIndex,
      selectedRouteIndices,
      carrierId,
      carrierName,
      transitIdentifier: transitIdentifier || undefined,
      scheduledDepartureDate: scheduledDeparture,
      scheduledArrivalDate: scheduledArrival,
    })
  }

  const handleOpenAddDestinationPanel = (legIndex: number, routeIndex: number, destIndex: number) => {
    setAddDestinationLegIndex(legIndex)
    setAddDestinationRouteIndex(routeIndex)
    setAddDestinationInsertAfterIndex(destIndex)
    setAddDestinationPanelOpen(true)
  }

  const handleCloseAddDestinationPanel = () => {
    setAddDestinationPanelOpen(false)
  }

  const handleAddDestination = (
    locationId: number,
    locationName: string | undefined,
    address: string | undefined,
    scheduledDate: string,
    loadActions: { loadId: number; destinationActionTypeId: number }[],
    loadServices: { loadId: number; serviceId: number }[]
  ) => {
    plannerDispatch({
      type: 'INSERT_DESTINATION_WITH_ACTIONS',
      legIndex: addDestinationLegIndex,
      routeIndex: addDestinationRouteIndex,
      insertAfterDestIndex: addDestinationInsertAfterIndex,
      locationId,
      locationName,
      address,
      scheduledDate,
      loadActions,
      loadServices,
    })
  }

  const handleOpenEditActionsPanel = (legIndex: number, routeIndex: number, destIndex: number) => {
    setEditActionsLegIndex(legIndex)
    setEditActionsRouteIndex(routeIndex)
    setEditActionsDestIndex(destIndex)
    setEditActionsPanelOpen(true)
  }

  const handleCloseEditActionsPanel = () => {
    setEditActionsPanelOpen(false)
  }

  const handleSaveEditedActions = (updatedActions: EditedLoadActionConfig[], removedLoadIds: number[]) => {
    plannerDispatch({
      type: 'UPDATE_LOAD_ACTIONS_AT_DESTINATION',
      legIndex: editActionsLegIndex,
      routeIndex: editActionsRouteIndex,
      destIndex: editActionsDestIndex,
      updatedActions,
      removedLoadIds,
    })
  }

  const handleSave = async () => {
    try {
      const payload = transformStateToApiPayload(plannerState)
      await saveDraftJourney({ tenantId, body: payload }).unwrap()
      enqueueSnackbar(formatMessage(messages.journeySavedSuccessfully), { variant: 'success' })
      dispatch(clearSelection())
      navigate(rootEnum.JOURNEY)
    } catch (error) {
      enqueueSnackbar(formatMessage(messages.journeySaveFailed), { variant: 'error' })
      console.error('Failed to save journey:', error)
    }
  }

  if (selectedLoads.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {formatMessage(messages.noLoadsSelectedForPlanning)}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBackToUnassignedLoads}
            sx={{ mt: 2 }}
          >
            {formatMessage(messages.backToUnassignedLoads)}
          </Button>
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{formatMessage(messages.journeyPlanner)}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={handleBackToUnassignedLoads}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {formatMessage(messages.saveJourney)}
          </Button>
        </Box>
      </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {plannerState.legs.map((leg, legIndex) => {
                            const waypointsForLeg = getWaypointsForLeg(legIndex)
                            let waypointDisplayNumber = 1
                            for (let i = 0; i < legIndex; i++) {
                              waypointDisplayNumber += getWaypointsForLeg(i).length
                            }
                            const isLastLeg = legIndex === plannerState.legs.length - 1

                            return (
                              <Box key={leg.id} sx={{ display: 'flex', gap: 2 }}>
                                {/* Timeline icon column with connecting line */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: 60,
                                    flexShrink: 0,
                                  }}
                                >
                                  {/* Icon centered with LegSection header */}
                                  <Box sx={{ height: 56, display: 'flex', alignItems: 'center' }}>
                                    <TransportModeIcon transportModeId={leg.transportModeId} />
                                  </Box>
                                  {/* Connecting line below icon (only if not last leg) */}
                                  {!isLastLeg && (
                                    <Box
                                      sx={{
                                        width: 2,
                                        flex: 1,
                                        bgcolor: 'grey.300',
                                        minHeight: 20,
                                      }}
                                    />
                                  )}
                                </Box>

                                {/* Leg content area */}
                                <Box sx={{ flex: 1 }}>
                                    <LegSection
                                      leg={leg}
                                      legIndex={legIndex}
                                      loadMetadataById={plannerState.loadMetadataById}
                                      dispatch={plannerDispatch}
                                      canRemove={plannerState.legs.length > 1}
                                      tenantId={tenantId}
                                      onSplitRouteClick={handleOpenSplitRoutePanel}
                                      onMergeRouteClick={handleOpenMergeRoutePanel}
                                      onAddDestinationClick={handleOpenAddDestinationPanel}
                                      onEditActionsClick={handleOpenEditActionsPanel}
                                      previousLegLastDate={legIndex > 0 ? (() => {
                                        const prevLeg = plannerState.legs[legIndex - 1]
                                        const lastRoute = prevLeg.routes[prevLeg.routes.length - 1]
                                        const lastDest = lastRoute?.destinations[lastRoute.destinations.length - 1]
                                        return lastDest?.scheduledDate ? dayjs(lastDest.scheduledDate) : undefined
                                      })() : undefined}
                                      services={services}
                                    />

                                  {waypointsForLeg.map((waypoint, wpIdx) => {
                                    const globalWpIndex = plannerState.waypoints.findIndex(
                                      (wp) => wp.id === waypoint.id
                                    )
                                    const legBeforeNum = legIndex + 1
                                    const legAfterNum = legIndex + 2
                                    return (
                                      <WaypointSection
                                        key={waypoint.id}
                                        waypoint={waypoint}
                                        waypointIndex={globalWpIndex}
                                        waypointDisplayNumber={waypointDisplayNumber + wpIdx}
                                        dispatch={plannerDispatch}
                                        onAddWaypointBefore={() => handleOpenAddWaypointPanel('before', legIndex)}
                                        onAddWaypointAfter={() => handleOpenAddWaypointPanel('after', legIndex)}
                                        onDelete={
                                          waypoint.isUserCreated
                                            ? () => handleOpenDeleteDialog(globalWpIndex, legBeforeNum, legAfterNum)
                                            : undefined
                                        }
                                      />
                                    )
                                  })}

                                  {legIndex < plannerState.legs.length - 1 && waypointsForLeg.length === 0 && (
                                    (() => {
                                      // Get the shared waypoint location between this leg and the next
                                      // The shared waypoint is the last destination of the current leg
                                      // (which should be the same as the first destination of the next leg)
                                      const currentLegRoutes = leg.routes
                                      const lastRoute = currentLegRoutes[currentLegRoutes.length - 1]
                                      const lastDestination = lastRoute?.destinations[lastRoute.destinations.length - 1]
                                      const sharedLocationName = lastDestination?.locationName || ''
                                      const sharedAddress = lastDestination?.address

                                      return (
                                        <SharedWaypointSection
                                          waypointNumber={legIndex + 1}
                                          locationName={sharedLocationName}
                                          address={sharedAddress}
                                          onAddWaypointBefore={() => handleOpenAddWaypointPanel('before', legIndex)}
                                          onAddWaypointAfter={() => handleOpenAddWaypointPanel('after', legIndex)}
                                        />
                                      )
                                    })()
                                  )}
                                </Box>
                              </Box>
                            )
                          })}

                          {/* Add Waypoint button - shown when no intermediate waypoints exist */}
                          {plannerState.legs.length > 0 && plannerState.waypoints.length === 0 && (
                            <Box sx={{ ml: 8 }}>
                              <Button
                                variant="outlined"
                                onClick={() => handleOpenAddWaypointPanel('after', plannerState.legs.length - 1)}
                              >
                                {formatMessage(messages.addWaypoint)}
                              </Button>
                            </Box>
                          )}
                        </Box>

      <AddWaypointSidePanel
        open={addWaypointPanelOpen}
        onClose={handleCloseAddWaypointPanel}
        onAdd={handleAddWaypointWithLeg}
        position={addWaypointPosition}
        legIndex={addWaypointLegIndex}
        tenantId={tenantId}
      />

      <DeleteWaypointDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteWaypoint}
        waypointNumber={deleteWaypointIndex + 1}
        legBeforeNumber={deleteLegBeforeNumber}
        legAfterNumber={deleteLegAfterNumber}
      />

      {splitRoutePanelOpen && plannerState.legs[splitRouteLegIndex]?.routes[splitRouteIndex] && (
        <SplitRouteSidePanel
          open={splitRoutePanelOpen}
          onClose={handleCloseSplitRoutePanel}
          onSplit={handleSplitRoute}
          route={plannerState.legs[splitRouteLegIndex].routes[splitRouteIndex]}
          loadMetadataById={plannerState.loadMetadataById}
          tenantId={tenantId}
          transportModeId={plannerState.legs[splitRouteLegIndex].transportModeId}
        />
      )}

      {mergeRoutePanelOpen && plannerState.legs[mergeRouteLegIndex] && (
        <MergeRouteSidePanel
          open={mergeRoutePanelOpen}
          onClose={handleCloseMergeRoutePanel}
          onMerge={handleMergeRoutes}
          routes={plannerState.legs[mergeRouteLegIndex].routes}
          loadMetadataById={plannerState.loadMetadataById}
          tenantId={tenantId}
          transportModeId={plannerState.legs[mergeRouteLegIndex].transportModeId}
        />
      )}

      {addDestinationPanelOpen && plannerState.legs[addDestinationLegIndex]?.routes[addDestinationRouteIndex] && (
        <AddDestinationSidePanel
          open={addDestinationPanelOpen}
          onClose={handleCloseAddDestinationPanel}
          onAdd={handleAddDestination}
          route={plannerState.legs[addDestinationLegIndex].routes[addDestinationRouteIndex]}
          loadMetadataById={plannerState.loadMetadataById}
          tenantId={tenantId}
          services={services}
          minDate={(() => {
            const route = plannerState.legs[addDestinationLegIndex]?.routes[addDestinationRouteIndex]
            const prevDest = route?.destinations[addDestinationInsertAfterIndex]
            return prevDest?.scheduledDate ? dayjs(prevDest.scheduledDate) : dayjs().startOf('day')
          })()}
        />
      )}

      {editActionsPanelOpen && plannerState.legs[editActionsLegIndex]?.routes[editActionsRouteIndex]?.destinations[editActionsDestIndex] && (
        <EditActionsServicesSidePanel
          open={editActionsPanelOpen}
          onClose={handleCloseEditActionsPanel}
          onSave={handleSaveEditedActions}
          destination={plannerState.legs[editActionsLegIndex].routes[editActionsRouteIndex].destinations[editActionsDestIndex]}
          loadMetadataById={plannerState.loadMetadataById}
          services={services}
        />
      )}
    </Box>
  )
}

export default JourneyPlannerPage
